import React, { useEffect, useMemo, useState } from "react"
import api from "../../utils/api"
import { formatMoney, formatDateShort, formatDateTimeEAT, toNairobiDate } from "../../utils/format"
import { Calendar, CreditCard, Clock } from "lucide-react"
import { getApiErrorMessage } from "../../utils/errorMessage"
import { getGroupTheme } from "../../utils/groupTheme"

const toNumber = (value) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const formatHoursLeftUntil = (value, referenceNow = new Date()) => {
  const target = toNairobiDate(value)
  const now = toNairobiDate(referenceNow)
  if (!target || !now) return ""

  const diffMs = target.getTime() - now.getTime()
  if (diffMs <= 0) return "due now"
  const totalMinutes = Math.ceil(diffMs / 60000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours <= 0) return `${minutes}m left`
  if (minutes === 0) return `${hours}h left`
  return `${hours}h ${minutes}m left`
}

const parseRefOrderValue = (referenceCode) => {
  const normalized = String(referenceCode || "").trim().toUpperCase()
  const match = normalized.match(/^[DL](\d+)$/)
  if (!match) return Number.MAX_SAFE_INTEGER
  return Number(match[1] || Number.MAX_SAFE_INTEGER)
}

const formatWelfareMemberIdentifier = (identifier, fallbackMemberNumber = 0) => {
  const raw = String(identifier || "").trim()
  if (raw) return raw.replace(/^#/i, "M")
  const number = Number(fallbackMemberNumber || 0)
  return number > 0 ? `M${number}` : ""
}

const WELFARE_RELATION_LABELS = {
  member: "Member",
  spouse: "Spouse",
  child: "Child",
  sibling: "Sibling",
  parent: "Parent",
  spouse_parent: "Spouse Parent",
}

const normalizeParentType = (value) => {
  const normalized = String(value || "").trim().toLowerCase()
  if (normalized === "father" || normalized === "mother") return normalized
  return ""
}

const resolveDisbursementRelationLabel = (row) => {
  const explicit = String(row?.relationLabel || "").trim()
  if (explicit) return explicit

  const relationType = String(row?.relationType || "").trim().toLowerCase()
  const parentType = normalizeParentType(row?.familyMemberParentType)
  if (relationType === "parent") {
    if (parentType === "father") return "Father"
    if (parentType === "mother") return "Mother"
  }
  if (relationType === "spouse_parent") {
    if (parentType === "father") return "Spouse Father"
    if (parentType === "mother") return "Spouse Mother"
  }
  return WELFARE_RELATION_LABELS[relationType] || "-"
}

function GroupSummaryCard({ group }) {
  const safeGroup = group || {}
  const theme = getGroupTheme(safeGroup)
  const GroupIcon = theme.icon
  const isRecurring = safeGroup.category === "recurring"
  const isProjectSavings = !!safeGroup?.isProjectSavings
  const isWelfare = !!safeGroup?.isWelfare
  const isChamaa = !!safeGroup?.isChamaa
  const activeCycle = safeGroup.activeCycle || null
  const nextDueDate = (() => {
    const orderedRounds = Array.isArray(activeCycle?.rounds)
      ? [...activeCycle.rounds].sort(
          (a, b) => Number(a?.roundNumber || 0) - Number(b?.roundNumber || 0)
        )
      : []
    const nextScheduledRound = orderedRounds.find((round) => round?.status === "scheduled") || null
    if (nextScheduledRound?.invoiceScheduledAt) return nextScheduledRound.invoiceScheduledAt

    const currentIssuedRound =
      [...orderedRounds].reverse().find((round) => round?.status === "invoice_issued") || null
    if (!currentIssuedRound) return null
    return (
      currentIssuedRound?.payoutScheduledAt ||
      currentIssuedRound?.invoiceDueDate ||
      currentIssuedRound?.invoiceScheduledAt ||
      null
    )
  })()
  const nextDueCountdownLabel = nextDueDate ? formatHoursLeftUntil(nextDueDate) : ""
  const nextDueDateTime = nextDueDate ? formatDateTimeEAT(nextDueDate) : "TBC"
  const roundsCompleted = Number(activeCycle?.roundsCompleted || 0)
  const totalRounds = Number(activeCycle?.totalRounds || 0)
  const progressPct =
    totalRounds > 0 ? Math.min(100, Math.round((roundsCompleted / totalRounds) * 100)) : 0
  const showRecurringCyclePanel = isRecurring && !!activeCycle && !isProjectSavings
  const displayPerShareAmount = Number(
    activeCycle?.contributionAmount ??
      safeGroup?.nextCycle?.contributionAmount ??
      safeGroup?.amountPerPerson ??
      safeGroup?.totalBudget ??
      0
  )
  const roundFrequencyLabel = String(
    activeCycle?.invoiceFrequency ||
      safeGroup?.nextCycle?.invoiceFrequency ||
      safeGroup?.frequency ||
      safeGroup?.invoiceFrequency ||
      safeGroup?.contribution?.longTerm?.frequency ||
      ""
  ).trim()
  const groupTypeLabel = String(safeGroup?.type || theme?.label || "").trim() || "-"

  const orderedCycleRounds = Array.isArray(activeCycle?.rounds)
    ? [...activeCycle.rounds].sort((a, b) => Number(a?.roundNumber || 0) - Number(b?.roundNumber || 0))
    : []
  const currentInvoiceRound =
    [...orderedCycleRounds].reverse().find((round) => String(round?.status || "") === "invoice_issued") || null
  const nextScheduledCycleRound =
    orderedCycleRounds.find((round) => String(round?.status || "") === "scheduled") || null
  const latestProcessedRound =
    [...orderedCycleRounds].reverse().find((round) => String(round?.status || "") === "payout_processed") || null
  const focusRound = currentInvoiceRound || latestProcessedRound || null
  const activeRoundNumber = Number(
    currentInvoiceRound?.roundNumber ||
      nextScheduledCycleRound?.roundNumber ||
      latestProcessedRound?.roundNumber ||
      0
  )
  const roundProgressLabel =
    totalRounds > 0
      ? `Round ${Math.min(totalRounds, Math.max(1, activeRoundNumber || roundsCompleted || 1))} of ${totalRounds}`
      : "Rounds TBC"

  const roundTotalIn = (() => {
    if (!focusRound) return 0
    const paidInvoiceCount = Number(focusRound?.paidInvoiceCount || 0)
    const perShareAmount = Number(displayPerShareAmount || 0)
    if (String(focusRound?.status || "") === "payout_processed") {
      const actualPayoutAmount = Number(focusRound?.actualPayoutAmount || 0)
      if (actualPayoutAmount > 0) return actualPayoutAmount
    }
    return Number((paidInvoiceCount * perShareAmount).toFixed(2))
  })()

  const currentShares = (() => {
    const activeParticipants = Array.isArray(activeCycle?.participants) ? activeCycle.participants : []
    if (activeParticipants.length > 0) {
      return activeParticipants.reduce((sum, participant) => sum + Number(participant?.shareCount || 0), 0)
    }
    if (Number.isFinite(Number(activeCycle?.totalShares)) && Number(activeCycle?.totalShares) > 0) {
      return Number(activeCycle.totalShares)
    }
    if (Array.isArray(safeGroup?.members)) {
      return safeGroup.members.reduce((sum, m) => sum + Number(m.desiredShares || 1), 0)
    }
    return 0
  })()

  const [isRepaymentModalOpen, setIsRepaymentModalOpen] = useState(false)
  const [isDisbursementModalOpen, setIsDisbursementModalOpen] = useState(false)
  const [isLoadingDisbursements, setIsLoadingDisbursements] = useState(false)
  const [disbursementsError, setDisbursementsError] = useState("")
  const [disbursementRows, setDisbursementRows] = useState([])
  const [totalBenefitDisbursed, setTotalBenefitDisbursed] = useState(0)
  const [disbursementSearch, setDisbursementSearch] = useState("")
  const [disbursementSort, setDisbursementSort] = useState("date_desc")

  useEffect(() => {
    setIsRepaymentModalOpen(false)
    setIsDisbursementModalOpen(false)
    setDisbursementRows([])
    setTotalBenefitDisbursed(0)
    setDisbursementSearch("")
    setDisbursementSort("date_desc")
    setDisbursementsError("")
  }, [group?.id])

  useEffect(() => {
    if (!isWelfare || !group?.id) return

    let isMounted = true
    const loadDisbursements = async () => {
      setIsLoadingDisbursements(true)
      setDisbursementsError("")
      try {
        const res = await api.get(`/groups/${group.id}/welfare/disbursements`)
        if (!isMounted) return
        const rows = Array.isArray(res?.data?.disbursements) ? res.data.disbursements : []
        setDisbursementRows(rows)
        setTotalBenefitDisbursed(toNumber(res?.data?.summary?.totalDisbursed))
      } catch (err) {
        if (!isMounted) return
        setDisbursementRows([])
        setTotalBenefitDisbursed(0)
        setDisbursementsError(
          getApiErrorMessage(err, "Failed to load welfare disbursements.")
        )
      } finally {
        if (isMounted) {
          setIsLoadingDisbursements(false)
        }
      }
    }

    loadDisbursements()
    return () => {
      isMounted = false
    }
  }, [group?.id, isWelfare])

  const repaymentRows = useMemo(() => {
    if (!isWelfare) return []
    return (safeGroup?.members || [])
      .map((member) => {
        const dueAmount = toNumber(member?.dueAmount ?? member?.balanceAmount ?? 0)
        const fallbackNumber = Number(member?.welfareMemberNumber || 0)
        const welfareMemberIdentifier = formatWelfareMemberIdentifier(
          member?.welfareMemberIdentifier,
          fallbackNumber
        )
        return {
          id: String(member?.id || ""),
          name: member?.name || "Member",
          welfareMemberIdentifier,
          dueAmount,
        }
      })
      .filter((member) => member.dueAmount > 0)
      .sort((a, b) => {
        const amountDiff = Number(b?.dueAmount || 0) - Number(a?.dueAmount || 0)
        if (amountDiff !== 0) return amountDiff
        return String(a?.name || "").localeCompare(String(b?.name || ""))
      })
  }, [safeGroup?.members, isWelfare])

  const repaymentGroupTotal = useMemo(
    () => repaymentRows.reduce((sum, row) => sum + toNumber(row?.dueAmount), 0),
    [repaymentRows]
  )

  const filteredDisbursementRows = useMemo(() => {
    const query = String(disbursementSearch || "").trim().toLowerCase()
    let rows = Array.isArray(disbursementRows) ? [...disbursementRows] : []

    if (query) {
      rows = rows.filter((row) => {
        const recipientMatch = String(row?.recipientName || "").toLowerCase().includes(query)
        const refMatch = String(row?.referenceCode || "").toLowerCase().includes(query)
        return recipientMatch || refMatch
      })
    }

    rows.sort((a, b) => {
      if (disbursementSort === "date_asc") {
        return new Date(a?.disbursedAt || 0).getTime() - new Date(b?.disbursedAt || 0).getTime()
      }
      if (disbursementSort === "date_desc") {
        return new Date(b?.disbursedAt || 0).getTime() - new Date(a?.disbursedAt || 0).getTime()
      }
      if (disbursementSort === "ref_asc") {
        const byRef = parseRefOrderValue(a?.referenceCode) - parseRefOrderValue(b?.referenceCode)
        if (byRef !== 0) return byRef
        return new Date(b?.disbursedAt || 0).getTime() - new Date(a?.disbursedAt || 0).getTime()
      }
      if (disbursementSort === "ref_desc") {
        const byRef = parseRefOrderValue(b?.referenceCode) - parseRefOrderValue(a?.referenceCode)
        if (byRef !== 0) return byRef
        return new Date(b?.disbursedAt || 0).getTime() - new Date(a?.disbursedAt || 0).getTime()
      }
      return 0
    })

    return rows
  }, [disbursementRows, disbursementSearch, disbursementSort])

  if (!group) return null

  return (
    <div className={`rounded-2xl border border-gray-200 shadow-sm p-4 space-y-4 max-w-full transition-colors duration-500 ${theme.cardBg}`}>
      <div className="flex flex-col gap-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <div className={`p-1.5 rounded-lg flex-shrink-0 ${theme.bg} ${theme.text}`}>
              <GroupIcon size={16} />
            </div>
            <div className="min-w-0 pr-2">
              <h2 className="text-sm font-bold text-gray-900 leading-tight break-words">{group.name}</h2>
              <p className="text-[10px] text-gray-500 mt-0.5">
                Group ID: <span className="font-semibold text-gray-700">{group?.groupCode || "-"}</span>
              </p>
              <p className="text-[10px] text-gray-500 mt-0.5">
                Group Type: <span className="font-semibold uppercase text-gray-700">{groupTypeLabel}</span>
              </p>
            </div>
          </div>
        </div>
        {showRecurringCyclePanel && isChamaa && (
          <div className={`${theme.mutedBg} rounded-xl p-3 border ${theme.border}/50 space-y-2.5`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <p className={`text-[10px] font-bold ${theme.accentText} uppercase tracking-wider`}>
                  Cycle #{activeCycle.sequenceNumber}
                </p>
                <span className={`px-1.5 py-0.5 rounded bg-white text-[9px] font-bold ${theme.text} border ${theme.border} shadow-sm uppercase`}>
                  {activeCycle.status}
                </span>
              </div>
              <p className={`text-[10px] font-medium ${theme.text}/70`}>
                {roundProgressLabel}
              </p>
            </div>
            <div className={`h-1.5 w-full ${theme.progressBg} rounded-full overflow-hidden`}>
              <div className={`h-full ${theme.progressFill} transition-all duration-500`} style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        )}
      </div>

      {!isWelfare && (
        <div className="grid grid-cols-2 gap-2">
          <div className={`${theme.bg}/30 p-2.5 rounded-xl border ${theme.border} flex items-center gap-2.5 min-w-0`}>
            <div className={`p-1.5 rounded-lg bg-white shadow-sm ${theme.text} flex-shrink-0`}>
              <Clock size={14} />
            </div>
            <div className="min-w-0">
              <p className="text-[9px] font-bold text-gray-400 uppercase tracking-tight">Next Due</p>
              <p className="text-[10px] font-bold text-gray-900 leading-tight whitespace-normal">
                {nextDueDateTime}
              </p>
              <p className="text-[9px] text-gray-500 leading-tight whitespace-normal">
                {nextDueCountdownLabel || "TBC"}
              </p>
            </div>
          </div>

          <div className={`${theme.bg}/30 p-2.5 rounded-xl border ${theme.border} flex items-center gap-2.5 min-w-0`}>
            <div className={`p-1.5 rounded-lg bg-white shadow-sm ${theme.text} flex-shrink-0`}>
              <CreditCard size={14} />
            </div>
            <div className="min-w-0">
              <p className="text-[9px] font-bold text-gray-400 uppercase tracking-tight">Per Person</p>
              <p className="text-[10px] font-bold text-gray-900 truncate">
                {formatMoney(activeCycle?.shareUnitAmount || displayPerShareAmount)}
              </p>
            </div>
          </div>

        </div>
      )}

      {showRecurringCyclePanel && !isChamaa && (
        <div className={`${theme.mutedBg} rounded-xl p-3 border ${theme.border}/50 space-y-2.5`}>
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <p className={`text-[10px] font-bold ${theme.accentText} uppercase tracking-wider`}>
                  Cycle #{activeCycle.sequenceNumber}
                </p>
                <span className={`px-1.5 py-0.5 rounded bg-white text-[9px] font-bold ${theme.text} border ${theme.border} shadow-sm uppercase`}>
                  {activeCycle.status}
                </span>
              </div>
              <p className={`text-[10px] font-medium ${theme.text}/70`}>
                {roundProgressLabel}
              </p>
            </div>

            {!isChamaa && <div className="flex items-center justify-between gap-2">
              <div className={`flex items-center gap-1.5 text-[10px] ${theme.mutedText} font-medium min-w-0`}>
                <Calendar size={10} />
                <span>{activeCycle?.startDate ? formatDateShort(activeCycle.startDate) : "TBC"}</span>
                <span className={`text-${theme.primary}/30`}>→</span>
                <span>{activeCycle?.endDate ? formatDateShort(activeCycle.endDate) : "TBC"}</span>
              </div>
              <div className="text-right shrink-0">
                <p className={`text-[10px] font-semibold ${theme.text}`}>{roundFrequencyLabel || "TBC"}</p>
              </div>
            </div>}
          </div>

          <div className={`h-1.5 w-full ${theme.progressBg} rounded-full overflow-hidden`}>
            <div className={`h-full ${theme.progressFill} transition-all duration-500`} style={{ width: `${progressPct}%` }} />
          </div>
        </div>
      )}

      {isWelfare ? (
        <div className="grid grid-cols-2 gap-2 pt-1 border-t border-gray-100">
          <button
            type="button"
            onClick={() => setIsRepaymentModalOpen(true)}
            className={`bg-white p-2.5 rounded-xl border border-gray-100 shadow-sm flex flex-col items-center text-center transition hover:bg-gray-50 group border-b-2 border-transparent ${theme.borderLeft.replace("border-l-", "hover:border-")}`}
          >
            <div className={`p-1.5 rounded-lg ${theme.bg} ${theme.text} mb-1 group-hover:${theme.border} transition`}>
              <CreditCard size={14} />
            </div>
            <p className="text-[9px] font-semibold text-gray-500 uppercase tracking-tight">RePayment Group</p>
            <p className="text-[10px] font-bold text-gray-900 mt-0.5">{formatMoney(repaymentGroupTotal)}</p>
          </button>
          <button
            type="button"
            onClick={() => setIsDisbursementModalOpen(true)}
            className={`bg-white p-2.5 rounded-xl border border-gray-100 shadow-sm flex flex-col items-center text-center transition hover:bg-gray-50 group border-b-2 border-transparent ${theme.borderLeft.replace("border-l-", "hover:border-")}`}
          >
            <div className={`p-1.5 rounded-lg ${theme.bg} ${theme.text} mb-1 group-hover:${theme.border} transition`}>
              <Clock size={14} />
            </div>
            <p className="text-[9px] font-semibold text-gray-500 uppercase tracking-tight">Total Benefit Disbursed</p>
            <p className="text-[10px] font-bold text-gray-900 mt-0.5">{formatMoney(totalBenefitDisbursed)}</p>
          </button>
        </div>
      ) : !isChamaa ? (
        <div className="flex items-center justify-between px-1 pt-1 border-t border-gray-100">
          <div className="space-y-0.5">
            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-tight">Round Total In</p>
            <p className="text-[11px] font-bold text-gray-900">{formatMoney(roundTotalIn)}</p>
          </div>
          <div className="text-right space-y-0.5">
            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-tight">Current Shares</p>
            <p className="text-[11px] font-bold text-gray-900">{currentShares}</p>
          </div>
        </div>
      ) : null}

      {isWelfare && isRepaymentModalOpen && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 p-3 sm:p-5">
          <div className="w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl bg-white border border-gray-200 shadow-2xl">
            <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-4 sm:px-5">
              <div className="min-w-0">
                <h4 className="text-base font-semibold text-gray-900">RePayment Group</h4>
                <p className="mt-1 text-xs text-gray-500">
                  Total owing: <span className="font-semibold text-red-600">{formatMoney(repaymentGroupTotal)}</span>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsRepaymentModalOpen(false)}
                className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-200"
              >
                Close
              </button>
            </div>
            <div className="max-h-[68vh] overflow-y-auto px-4 pt-4 pb-6 sm:px-5">
              {repaymentRows.length === 0 ? (
                <p className="text-xs text-gray-500">No members currently owe any amount.</p>
              ) : (
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                  <table className="w-full table-fixed text-left border-collapse text-[10px] sm:text-xs">
                    <thead className={`${theme.bg} ${theme.text} font-semibold border-b ${theme.border}`}>
                      <tr>
                        <th className="w-[68%] px-2 py-2 sm:px-3">Member</th>
                        <th className="w-[32%] px-2 py-2 sm:px-3 text-right">Owing</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-gray-600">
                      {repaymentRows.map((row) => (
                        <tr key={row.id} className="hover:bg-gray-50">
                          <td className="px-2 py-2 sm:px-3 text-gray-900 font-medium whitespace-normal break-words">
                            {row.name}
                            {row.welfareMemberIdentifier ? (
                              <span className="text-gray-500"> ({row.welfareMemberIdentifier})</span>
                            ) : null}
                          </td>
                          <td className="px-2 py-2 sm:px-3 text-right font-semibold text-red-600 whitespace-nowrap">
                            {formatMoney(row.dueAmount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isWelfare && isDisbursementModalOpen && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 p-3 sm:p-5">
          <div className="w-full max-w-5xl max-h-[92vh] overflow-hidden rounded-2xl bg-white border border-gray-200 shadow-2xl">
            <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-4 sm:px-5">
              <div className="min-w-0">
                <h4 className="text-base font-semibold text-gray-900">Loss Reports Disbursed</h4>
                <p className="mt-1 text-xs text-gray-500">
                  Total benefit disbursed:{" "}
                  <span className="font-semibold text-gray-800">{formatMoney(totalBenefitDisbursed)}</span>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsDisbursementModalOpen(false)}
                className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-200"
              >
                Close
              </button>
            </div>

            <div className="px-4 py-4 sm:px-5 border-b border-gray-200 bg-gray-50/80">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-gray-600 uppercase mb-1">
                    Search By Name
                  </label>
                  <input
                    type="text"
                    value={disbursementSearch}
                    onChange={(e) => setDisbursementSearch(e.target.value)}
                    placeholder="Type recipient name or ref..."
                    className={`w-full px-3 py-2.5 border rounded-lg text-xs focus:ring-2 outline-none bg-white ${theme.border} ${theme.ring}`}
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-600 uppercase mb-1">
                    Sort
                  </label>
                  <select
                    value={disbursementSort}
                    onChange={(e) => setDisbursementSort(e.target.value)}
                    className={`w-full px-3 py-2.5 border rounded-lg text-xs focus:ring-2 outline-none bg-white ${theme.border} ${theme.ring}`}
                  >
                    <option value="date_desc">Date (Newest)</option>
                    <option value="date_asc">Date (Oldest)</option>
                    <option value="ref_asc">Ref # (Ascending)</option>
                    <option value="ref_desc">Ref # (Descending)</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <p className="text-xs text-gray-600">
                    Showing <span className="font-semibold text-gray-900">{filteredDisbursementRows.length}</span> of{" "}
                    <span className="font-semibold text-gray-900">{disbursementRows.length}</span> rows
                  </p>
                </div>
              </div>
            </div>

            <div className="max-h-[62vh] overflow-y-auto overflow-x-hidden px-4 pt-4 pb-6 sm:px-5">
              {isLoadingDisbursements ? (
                <p className="text-xs text-gray-500">Loading disbursements...</p>
              ) : disbursementsError ? (
                <p className="text-xs text-red-600">{disbursementsError}</p>
              ) : filteredDisbursementRows.length === 0 ? (
                <p className="text-xs text-gray-500">No disbursements found for selected filters.</p>
              ) : (
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                  <table className="w-full table-fixed text-left border-collapse text-[10px] sm:text-xs">
                    <thead className={`${theme.bg} ${theme.text} font-semibold border-b ${theme.border} sticky top-0`}>
                      <tr>
                        <th className="w-[18%] px-2 py-2 sm:px-3">Date</th>
                        <th className="w-[28%] px-2 py-2 sm:px-3">Recipient</th>
                        <th className="w-[18%] px-2 py-2 sm:px-3">Relation</th>
                        <th className="w-[14%] px-2 py-2 sm:px-3">Ref #</th>
                        <th className="w-[22%] px-2 py-2 sm:px-3 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-gray-600">
                      {filteredDisbursementRows.map((row) => (
                        <tr key={row.id} className="hover:bg-gray-50">
                          <td className="px-2 py-2 sm:px-3 whitespace-nowrap">
                            {row?.disbursedAt ? formatDateShort(row.disbursedAt) : "-"}
                          </td>
                          <td className="px-2 py-2 sm:px-3 text-gray-900 font-medium whitespace-normal break-words">
                            {row?.recipientName || "-"}
                            {row?.recipientWelfareMemberIdentifier ? (
                              <span className="text-gray-500"> ({row.recipientWelfareMemberIdentifier})</span>
                            ) : null}
                          </td>
                          <td className="px-2 py-2 sm:px-3 whitespace-normal break-words">
                            {resolveDisbursementRelationLabel(row)}
                          </td>
                          <td className="px-2 py-2 sm:px-3 whitespace-nowrap">
                            {String(row?.referenceCode || "").trim() ? (
                              <span className="inline-flex items-center rounded-md border border-slate-200 bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold text-slate-700">
                                {String(row.referenceCode).trim()}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-2 py-2 sm:px-3 text-right whitespace-nowrap font-semibold text-gray-900">
                            {formatMoney(row?.amount || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GroupSummaryCard
